/**
 * The mode switch as an account-menu row, for the drawer.
 *
 * Below `navtab` the top bar is gone and the hamburger drawer is the whole of
 * the navigation, so the switch joins the rows already there rather than
 * competing for space in the mobile bar beside the logo and search. It reads
 * like its neighbours — "Switch to dark mode", "Switch to Teacher mode" — and
 * keeps the arrows it wears on the bar.
 *
 * Renders nothing for a viewer entitled to one mode, on the same rule as the
 * bar's button: a switch to where you already are is not a choice.
 */
import { ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';

import { modeHomeFor } from './mode-home';
import NavMenuItem from './NavMenuItem';
import type { AppModeInfo } from './use-app-mode';
import { modeLabel } from './viewMode';

export interface ModeSwitchItemProps {
  viewer: AppModeInfo;
  /** Called before navigating, so the drawer dismisses. */
  onNavigate: () => void;
}

export default function ModeSwitchItem({ viewer, onNavigate }: ModeSwitchItemProps) {
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();

  const target = viewer.availableModes.find((m) => m !== viewer.mode);
  if (!target) return null;

  return (
    <NavMenuItem
      icon={ArrowLeftRight}
      label={`Switch to ${modeLabel(target)} mode`}
      onClick={() => {
        onNavigate();
        navigate(modeHomeFor(target, hasAnyPermission));
      }}
    />
  );
}
